import { ArrowDown, ArrowUp, Activity } from "lucide-react";
import { useMarketData, formatPrice, type MarketInstrument } from "@/hooks/useMarketData";
import { cn } from "@/lib/utils";

/**
 * Bloomberg-style live market strip for the hero.
 * Auto-refreshes every 60s via React Query (see useMarketData).
 */

const InstrumentCell = ({ inst }: { inst: MarketInstrument }) => {
  const up = inst.changePct >= 0;
  return (
    <div className="flex items-center gap-2.5 whitespace-nowrap">
      <span className="font-display text-[13px] font-semibold tracking-wide text-foreground">
        {inst.symbol}
      </span>
      <span className="font-mono text-[13px] tabular-nums text-foreground/90">
        {formatPrice(inst.price, inst.kind)}
      </span>
      <span
        className={cn(
          "inline-flex items-center gap-0.5 font-mono text-[11px] font-semibold tabular-nums",
          up ? "text-primary" : "text-destructive",
        )}
      >
        {up ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
        {Math.abs(inst.changePct).toFixed(2)}%
      </span>
    </div>
  );
};

const MarketTicker = () => {
  const { all, isLoading } = useMarketData();

  return (
    <div
      className="rounded-xl border border-border/60 bg-card/70 backdrop-blur-sm shadow-sm"
      role="region"
      aria-label="Live market data"
    >
      <div className="flex items-stretch">
        {/* Live label */}
        <div className="flex items-center gap-1.5 px-3 py-2.5 border-r border-border/60 shrink-0">
          <span className="relative flex h-2 w-2" aria-hidden>
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-60" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
          </span>
          <span className="font-display text-[10px] font-bold uppercase tracking-[0.12em] text-primary">
            Live
          </span>
        </div>

        {/* Static rail — flex-wrap on mobile, single row on desktop. No marquee per brand rules. */}
        <div className="flex-1 py-2.5 overflow-x-auto scrollbar-none">
          {isLoading || all.length === 0 ? (
            <div className="flex items-center gap-2 px-4 text-xs text-muted-foreground">
              <Activity className="h-3.5 w-3.5 animate-pulse" />
              Loading market data…
            </div>
          ) : (
            <div className="flex gap-x-6 px-4 min-w-max">
              {all.map((inst) => (
                <InstrumentCell key={inst.symbol} inst={inst} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MarketTicker;
